import { useAuthStore } from '../auth/store';

export class ApiError extends Error {
  status: number;
  code: string;
  details?: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

const BASE = '/api/v1';
let refreshing: Promise<string | null> | null = null;

async function tryRefresh(): Promise<string | null> {
  if (refreshing) return refreshing;
  const { refreshToken, setSession, clear } = useAuthStore.getState();
  if (!refreshToken) return null;
  refreshing = (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) {
        clear();
        return null;
      }
      const body = (await res.json()) as { data: { accessToken: string; refreshToken: string; user: any } };
      setSession(body.data);
      return body.data.accessToken;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

interface RequestOpts {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  searchParams?: Record<string, string | number | undefined>;
  skipAuth?: boolean;
}

async function exec<T>(path: string, opts: RequestOpts, retried = false): Promise<T> {
  const token = useAuthStore.getState().accessToken;
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (token && !opts.skipAuth) headers.Authorization = `Bearer ${token}`;

  let url = `${BASE}${path}`;
  if (opts.searchParams) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(opts.searchParams)) {
      if (v !== undefined && v !== null) qs.set(k, String(v));
    }
    const s = qs.toString();
    if (s) url += `?${s}`;
  }

  const res = await fetch(url, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  if (res.status === 401 && !retried && !opts.skipAuth) {
    const newToken = await tryRefresh();
    if (newToken) return exec<T>(path, opts, true);
  }

  if (!res.ok) {
    let payload: any = null;
    try {
      payload = await res.json();
    } catch {
      // ignore
    }
    const err = payload?.error ?? {};
    throw new ApiError(res.status, err.code ?? 'http_error', err.message ?? res.statusText, err.details);
  }

  if (res.status === 204) return undefined as T;
  const json = (await res.json()) as { data: T };
  return json.data;
}

export const api = {
  get: <T,>(path: string, params?: RequestOpts['searchParams'], signal?: AbortSignal) =>
    exec<T>(path, { method: 'GET', searchParams: params, signal }),
  post: <T,>(path: string, body?: unknown, opts?: { skipAuth?: boolean }) =>
    exec<T>(path, { method: 'POST', body, skipAuth: opts?.skipAuth }),
  patch: <T,>(path: string, body?: unknown) => exec<T>(path, { method: 'PATCH', body }),
  put: <T,>(path: string, body?: unknown) => exec<T>(path, { method: 'PUT', body }),
  delete: <T,>(path: string, body?: unknown) => exec<T>(path, { method: 'DELETE', body }),
};
